import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, SeverityDot, Empty } from "../components/ui-helpers";
import { RequirementHover } from "../components/RequirementHover";
import DefinitionsRegister from "../components/DefinitionsRegister";
import GlossaryText from "../components/GlossaryText";
import { ArrowRight, PlusCircle, BellRinging, UsersThree, CaretDown, CalendarCheck, ArrowsLeftRight, Sparkle } from "@phosphor-icons/react";
import { DataResidencyNote } from "../components/DataResidencyNote";

const ROLE_FIELDS = [["sme_primary", "SME Primary"], ["sme_backup", "SME Backup"], ["compliance_primary", "Compliance Primary"], ["compliance_backup", "Compliance Backup"]];
const BLANK_ASSIGN = { standard_id: "", sme_primary: "", sme_backup: "", compliance_primary: "", compliance_backup: "", only_empty: false };

const daysUntil = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / 86400000);
};
const untilLabel = (du) => {
  if (du === null) return "";
  if (du <= 0) return "now enforceable";
  if (du <= 180) return `in ${du} day${du === 1 ? "" : "s"}`;
  if (du <= 365) return `in ~${Math.round(du / 30)} months`;
  return `in ~${(du / 365).toFixed(du < 730 ? 1 : 0)} year${du < 730 ? "" : "s"}`;
};

export default function Gaps() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [standards, setStandards] = useState([]);
  const [sev, setSev] = useState("all");
  const [notifying, setNotifying] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assign, setAssign] = useState(BLANK_ASSIGN);
  const [assigning, setAssigning] = useState(false);
  const [futureRows, setFutureRows] = useState(null);
  const [transitions, setTransitions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [cStd, setCStd] = useState("");
  const [fStd, setFStd] = useState("");
  const [tName, setTName] = useState("");
  const nav = useNavigate();
  const { canEdit } = useAuth();

  const loadTransitions = useCallback(() => {
    api.get("/transitions").then((r) => setTransitions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadTransitions(); }, [loadTransitions]);
  useEffect(() => { api.get("/standards").then((r) => setStandards(r.data)).catch(() => {}); }, []);

  const startPrepare = (futureStd) => {
    setFStd(futureStd || ""); setCStd(""); setTName(""); setShowCreate(true);
    setTimeout(() => document.getElementById("transitions-section")?.scrollIntoView({ behavior: "smooth" }), 50);
  };
  const createTransition = async () => {
    if (!cStd || !fStd) return toast.error("Select both a From and a To standard.");
    if (cStd === fStd) return toast.error("Pick two different standards to compare.");
    try {
      const { data } = await api.post("/transitions", { current_standard_id: cStd, future_standard_id: fStd, name: tName });
      nav(`/transitions/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = useCallback(() => {
    api.get("/gaps").then((r) => setRows(r.data));
    api.get("/gaps/summary").then((r) => setSummary(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Future enforceable standards/requirements applicable to the active registration.
  // Grouped by standard, but each requirement (and subpart) carries its own
  // enforcement date. Superseded items are excluded (left at superseded).
  useEffect(() => {
    api.get("/requirements").then((r) => {
      const fut = (r.data || []).filter((x) => x.status === "future_enforceable" && x.applicable !== false);
      const byStd = {};
      fut.forEach((x) => {
        const k = x.standard_id;
        if (!byStd[k]) byStd[k] = { standard_id: k, effective_date: "", reqs: [], first_req_id: x.id };
        const subparts = (x.sub_requirements || []).map((sp) => ({ label: sp.label, effective_date: sp.effective_date || x.effective_date || "" }));
        byStd[k].reqs.push({ id: x.id, req_number: x.req_number, effective_date: x.effective_date || "", text: x.text || "", subparts });
        if (x.effective_date && (!byStd[k].effective_date || x.effective_date < byStd[k].effective_date)) byStd[k].effective_date = x.effective_date;
      });
      const list = Object.values(byStd).map((s) => {
        s.reqs.sort((a, b) => String(a.req_number).localeCompare(String(b.req_number), undefined, { numeric: true }));
        return s;
      }).sort((a, b) => (a.effective_date || "9999-99-99").localeCompare(b.effective_date || "9999-99-99"));
      setFutureRows(list);
    }).catch(() => setFutureRows([]));
  }, []);

  const createTask = async (row, gap) => {
    try {
      await api.post("/tasks", {
        title: `Evidence: ${row.standard_id} ${row.req_number}`,
        description: gap.prompt || gap.message, assignee: row.roles?.sme_primary || gap.owner || "",
        priority: gap.severity === "high" ? "high" : "medium",
        task_type: "evidence_request", related_type: "requirement", related_id: row.requirement_id,
      });
      toast.success("Follow-up task created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const notifyOwners = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post("/gaps/notify-owners");
      toast.success(`Notified assignees: ${data.created} new, ${data.skipped} already pending`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setNotifying(false); }
  };

  const runAssign = async () => {
    if (!assign.standard_id) { toast.error("Pick a standard first"); return; }
    setAssigning(true);
    try {
      const { data } = await api.post("/requirements/bulk-assign-roles", assign);
      toast.success(`Assigned roles to ${data.updated}/${data.total} requirements in ${assign.standard_id}`);
      setAssign(BLANK_ASSIGN); setShowAssign(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAssigning(false); }
  };

  if (!rows) return <div className="p-8 font-mono text-sm">Loading…</div>;

  const filtered = sev === "all" ? rows : rows.map((r) => ({ ...r, gaps: r.gaps.filter((g) => g.severity === sev) })).filter((r) => r.gaps.length);
  const totalGaps = rows.reduce((a, r) => a + r.gaps.length, 0);

  return (
    <div>
      <PageHeader title="NERC Readiness" subtitle={`${totalGaps} evidence gaps across ${rows.length} requirements — SMEs do the work, Compliance verifies`} />
      <div className="p-8 space-y-6">
        <DataResidencyNote />
        {futureRows && (
          <div className="border border-blue-200 bg-blue-50/50" data-testid="future-enforceable">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-blue-200 bg-blue-100/40">
              <CalendarCheck size={18} weight="fill" className="text-blue-700" />
              <h3 className="font-display font-bold text-[#071B3E]">Future Enforceable Standards &amp; Requirements</h3>
              <span className="text-xs text-muted-foreground hidden sm:inline">— coming into effect for your registration</span>
              {futureRows.length > 0 && <span className="ml-auto text-xs font-mono bg-blue-700 text-white px-2 py-0.5">{futureRows.length}</span>}
            </div>
            {futureRows.length === 0 ? (
              <div className="px-5 py-4 text-sm text-muted-foreground" data-testid="future-empty">
                No future-enforceable standards are currently scheduled for your registration.
              </div>
            ) : (
              <div className="divide-y divide-blue-100">
                {futureRows.map((s) => {
                  const du = daysUntil(s.effective_date);
                  const soon = du !== null && du <= 180;
                  return (
                    <div key={s.standard_id} data-testid={`future-std-${s.standard_id}`} className="px-5 py-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono font-bold text-sm bg-[#071B3E] text-white px-2 py-0.5 shrink-0">{s.standard_id}</span>
                          <span className="text-sm text-muted-foreground">
                            {s.reqs.length} requirement{s.reqs.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <div className="text-right">
                            <div className="text-sm font-semibold">{s.effective_date ? `Earliest ${s.effective_date}` : "Effective date TBD"}</div>
                            {du !== null && (
                              <div className={`text-xs ${soon ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}>{untilLabel(du)}</div>
                            )}
                          </div>
                          {canEdit && (
                            <button data-testid={`future-prepare-${s.standard_id}`} onClick={() => startPrepare(s.standard_id)}
                              className="flex items-center gap-1 text-sm font-semibold text-blue-700 border border-blue-300 px-2 py-1 hover:bg-blue-100">
                              <Sparkle size={13} weight="fill" /> Prepare</button>
                          )}
                        </div>
                      </div>
                      {/* Per-requirement (and subpart) enforcement dates */}
                      <div className="mt-2 ml-1 border-l-2 border-blue-200 pl-3 space-y-1.5">
                        {s.reqs.map((rq) => {
                          const rdu = daysUntil(rq.effective_date);
                          return (
                            <div key={rq.id} data-testid={`future-req-${s.standard_id}-${rq.req_number}`}>
                              <button onClick={() => nav(`/requirements/${rq.id}`)}
                                className="w-full flex items-center justify-between gap-3 text-left group">
                                <span className="text-sm min-w-0 truncate">
                                  <span className="font-mono font-semibold text-[#071B3E] group-hover:underline">{rq.req_number}</span>
                                  <span className="text-muted-foreground"> — {rq.text}</span>
                                </span>
                                <span className="shrink-0 text-xs">
                                  {rq.effective_date
                                    ? <span className="font-semibold text-blue-700">{rq.effective_date}{rdu !== null && rdu <= 180 ? ` · ${untilLabel(rdu)}` : ""}</span>
                                    : <span className="text-muted-foreground">date TBD</span>}
                                </span>
                              </button>
                              {rq.subparts.filter((sp) => sp.effective_date && sp.effective_date !== rq.effective_date).length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1 ml-3">
                                  {rq.subparts.filter((sp) => sp.effective_date && sp.effective_date !== rq.effective_date).map((sp) => (
                                    <span key={sp.label} data-testid={`future-subpart-${s.standard_id}-${rq.req_number}-${sp.label}`}
                                      className="text-[10px] border border-blue-200 bg-card text-blue-700 px-1.5 py-0.5">
                                      <span className="font-mono font-semibold">{sp.label}</span> · {sp.effective_date}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Definitions & defined terms register */}
        <DefinitionsRegister />

        {/* Standard Transition Workspaces (STaR) */}
        <div id="transitions-section" className="border border-border" data-testid="transitions-section">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-muted/40">
            <ArrowsLeftRight size={18} className="text-[#071B3E]" />
            <h3 className="font-display font-bold text-[#071B3E]">Standard Transition Workspaces <span className="font-normal text-sm text-muted-foreground">(STaR — Standard Tracking and Review form)</span></h3>
            <span className="text-xs text-muted-foreground hidden sm:inline">— compare current vs. future, requirement-by-requirement, for SME prep</span>
            {canEdit && (
              <button onClick={() => setShowCreate((v) => !v)} data-testid="new-transition-btn"
                className="ml-auto flex items-center gap-1 text-sm font-semibold text-white bg-[#071B3E] px-3 py-1 hover:bg-[#0c2a5e]">
                <PlusCircle size={15} weight="fill" /> New transition</button>
            )}
          </div>

          <div className="px-5 pt-3">
            <DataResidencyNote context="STaR is designed to analyze public standards." />
          </div>

          {canEdit && showCreate && (
            <div className="px-5 py-4 border-b border-border bg-blue-50/40 grid md:grid-cols-4 gap-3 items-end" data-testid="create-transition-form">
              <div>
                <label className="overline text-muted-foreground">From (current or future)</label>
                <select value={cStd} onChange={(e) => setCStd(e.target.value)} data-testid="create-current-std"
                  className="w-full mt-1 border border-border px-2 py-1.5 text-sm bg-card">
                  <option value="">Select…</option>
                  <optgroup label="Current / Enforceable">
                    {standards.filter((s) => s.status !== "future_enforceable" && s.standard_id !== fStd).map((s) => <option key={s.standard_id} value={s.standard_id}>{s.standard_id}</option>)}
                  </optgroup>
                  <optgroup label="Future Enforceable">
                    {standards.filter((s) => s.status === "future_enforceable" && s.standard_id !== fStd).map((s) => <option key={s.standard_id} value={s.standard_id}>{s.standard_id}</option>)}
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="overline text-muted-foreground">To (future)</label>
                <select value={fStd} onChange={(e) => setFStd(e.target.value)} data-testid="create-future-std"
                  className="w-full mt-1 border border-border px-2 py-1.5 text-sm bg-card">
                  <option value="">Select…</option>
                  {standards.filter((s) => s.status === "future_enforceable" && s.standard_id !== cStd).map((s) => <option key={s.standard_id} value={s.standard_id}>{s.standard_id}</option>)}
                </select>
              </div>
              <div>
                <label className="overline text-muted-foreground">Name (optional)</label>
                <input value={tName} onChange={(e) => setTName(e.target.value)} placeholder="e.g. CIP-003 low-impact transition"
                  className="w-full mt-1 border border-border px-2 py-1.5 text-sm" data-testid="create-transition-name" />
              </div>
              <button onClick={createTransition} data-testid="create-transition-submit"
                className="flex items-center justify-center gap-1 text-sm font-semibold text-white bg-blue-700 px-3 py-2 hover:bg-blue-800">Create &amp; open</button>
            </div>
          )}

          {transitions.length === 0 ? (
            <div className="px-5 py-4 text-sm text-muted-foreground" data-testid="transitions-empty">No transition workspaces yet. {canEdit ? "Create one to compare a current and future standard." : ""}</div>
          ) : (
            <div className="divide-y divide-border">
              {transitions.map((tr) => (
                <button key={tr.id} onClick={() => nav(`/transitions/${tr.id}`)} data-testid={`transition-${tr.id}`}
                  className="w-full flex items-center justify-between gap-4 px-5 py-3 text-left hover:bg-muted/40">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{tr.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{tr.current_standard_id} → {tr.future_standard_id}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs px-2 py-0.5 border ${tr.status === "ready" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : tr.status === "analyzing" ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-muted text-muted-foreground border-border"}`}>{tr.status}</span>
                    <ArrowRight size={15} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {summary && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border border border-border" data-testid="gap-summary">
            <div className="bg-card p-5">
              <div className="overline text-muted-foreground">By Severity</div>
              <div className="flex gap-5 mt-2">
                <div><span className="font-display font-black text-3xl text-red-600">{summary.by_severity.high}</span><div className="text-xs text-muted-foreground">High</div></div>
                <div><span className="font-display font-black text-3xl text-amber-600">{summary.by_severity.medium}</span><div className="text-xs text-muted-foreground">Medium</div></div>
              </div>
            </div>
            <div className="bg-card p-5">
              <div className="overline text-muted-foreground">Top SMEs (Doers)</div>
              <ul className="mt-2 space-y-1 text-sm">
                {summary.by_owner.slice(0, 4).map((o) => (
                  <li key={o.owner} className="flex justify-between"><span className="truncate pr-2">{o.owner}</span><span className="font-mono font-bold">{o.count}</span></li>
                ))}
                {summary.by_owner.length === 0 && <li className="text-muted-foreground">—</li>}
              </ul>
            </div>
            <div className="bg-card p-5">
              <div className="overline text-muted-foreground">Most-Affected Standards</div>
              <ul className="mt-2 space-y-1 text-sm">
                {summary.by_standard.slice(0, 4).map((s) => (
                  <li key={s.standard_id} className="flex justify-between"><span className="font-mono">{s.standard_id}</span><span className="font-mono font-bold">{s.count}</span></li>
                ))}
                {summary.by_standard.length === 0 && <li className="text-muted-foreground">—</li>}
              </ul>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1">
            {["all", "high", "medium"].map((s) => (
              <button key={s} data-testid={`filter-sev-${s}`} onClick={() => setSev(s)}
                className={`px-3 py-1.5 text-sm font-semibold border ${sev === s ? "bg-primary text-primary-foreground border-black" : "border-border hover:bg-secondary"}`}>
                {s === "all" ? "All" : s[0].toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button data-testid="toggle-assign-btn" onClick={() => setShowAssign((v) => !v)}
                className="flex items-center gap-2 border border-border px-3 py-1.5 text-sm font-semibold hover:bg-secondary">
                <UsersThree size={15} /> Bulk assign roles <CaretDown size={13} />
              </button>
              <button data-testid="notify-owners-btn" onClick={notifyOwners} disabled={notifying}
                className="flex items-center gap-2 border border-black bg-primary text-primary-foreground px-3 py-1.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
                <BellRinging size={15} weight="fill" />{notifying ? "Notifying…" : "Notify assignees"}
              </button>
            </div>
          )}
        </div>

        {canEdit && showAssign && (
          <div className="border border-border bg-secondary/30 p-5" data-testid="bulk-assign-panel">
            <div className="font-display font-bold mb-1">Assign roles to every requirement in a standard</div>
            <p className="text-xs text-muted-foreground mb-3">SMEs perform the work and collect evidence; Compliance verifies. Leave a field blank to skip it.</p>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Standard</label>
                <select data-testid="assign-standard-select" value={assign.standard_id} onChange={(e) => setAssign({ ...assign, standard_id: e.target.value })}
                  className="w-full border border-border px-2 py-1.5 text-sm mt-0.5 bg-card">
                  <option value="">Select…</option>
                  {standards.map((s) => <option key={s.standard_id} value={s.standard_id}>{s.standard_id}</option>)}
                </select>
              </div>
              {ROLE_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</label>
                  <input data-testid={`assign-${key}`} value={assign[key]} onChange={(e) => setAssign({ ...assign, [key]: e.target.value })}
                    placeholder={label} className="w-full border border-border px-2 py-1.5 text-sm mt-0.5" />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input data-testid="assign-only-empty" type="checkbox" checked={assign.only_empty} onChange={(e) => setAssign({ ...assign, only_empty: e.target.checked })} />
                Only fill requirements that are currently unassigned (don't overwrite)
              </label>
              <button data-testid="run-assign-btn" onClick={runAssign} disabled={assigning}
                className="border border-black bg-primary text-primary-foreground px-4 py-1.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">{assigning ? "Assigning…" : "Apply"}</button>
            </div>
          </div>
        )}

        {filtered.length === 0 ? <Empty title="No gaps detected" hint="Every requirement has mapped controls and evidence." /> : filtered.map((row) => (
          <div key={row.requirement_id} data-testid={`gap-req-${row.req_number}`} className="border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-5 py-3 bg-secondary/40 gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <RequirementHover label={`${row.standard_id} ${row.req_number}`} text={row.text}>
                  <span className="font-mono font-bold text-sm bg-primary text-primary-foreground px-2 py-0.5 cursor-help">{row.standard_id} {row.req_number}</span>
                </RequirementHover>
                <GlossaryText text={row.text} className="text-sm text-muted-foreground max-w-md truncate" />
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[11px] font-mono text-muted-foreground" data-testid={`gap-roles-${row.req_number}`}>
                  SME: <span className="font-semibold text-foreground">{row.roles?.sme_primary || "Unassigned"}</span>
                  {" · "}Compliance: <span className="font-semibold text-foreground">{row.roles?.compliance_primary || "Unassigned"}</span>
                </span>
                <button data-testid={`open-req-${row.req_number}`} onClick={() => nav(`/requirements/${row.requirement_id}`)} className="flex items-center gap-1 text-sm font-semibold hover:text-blue-600">Open <ArrowRight size={14} /></button>
              </div>
            </div>
            <div className="divide-y divide-border">
              {row.gaps.map((g, i) => (
                <div key={`${row.requirement_id}-${g.type}-${i}`} className="flex items-start justify-between gap-4 px-5 py-3">
                  <div className="flex items-start gap-3">
                    <SeverityDot severity={g.severity} />
                    <div>
                      <GlossaryText text={g.message} className="text-sm" />
                      {g.prompt && <p className="text-xs text-muted-foreground mt-0.5" data-testid={`gap-prompt-${row.req_number}-${i}`}>→ <GlossaryText text={g.prompt} /></p>}
                    </div>
                  </div>
                  {canEdit && <button data-testid={`create-task-${row.req_number}-${i}`} onClick={() => createTask(row, g)} className="flex items-center gap-1 border border-border px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white shrink-0"><PlusCircle size={14} />Create task</button>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
